import React, { useState, useEffect, useCallback } from "react";
import API_BASE from "./apiConfig";
import { useTheme, getT, getBrandColors} from "./ThemeContext";
import CoachAvailabilityView from "./CoachAvailabilityView";
import { useSort } from "./hooks/useSort";
import { useDateTimeFormat } from "./utils/formatters";
import { useAppConfig } from "./AppContext";
import StatusBadge from "./components/StatusBadge";

const DAYS   = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const MONTHS = ["January","February","March","April","May","June",
                "July","August","September","October","November","December"];
const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const STATUS_META = {
  pending_payment: { label: "Pending Payment", tone: "warning" },
  confirmed:       { label: "Confirmed",       tone: "success" },
  checked_in:      { label: "Checked In",      tone: "info" },
  completed:       { label: "Completed",        tone: "info" },
  cancelled:       { label: "Cancelled",        tone: "neutral" },
  no_show:         { label: "No Show",          tone: "warning" },
};

function buildCalendar(year, month) {
  const firstDay    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startOffset = (firstDay + 6) % 7;
  const weeks = [];
  let week = Array(startOffset).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    week.push(d);
    if (week.length === 7) { weeks.push(week); week = []; }
  }
  if (week.length) { while (week.length < 7) week.push(null); weeks.push(week); }
  return weeks;
}

export default function OTOParentBooking({ currentUser }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const { fmtDate, fmtTime } = useDateTimeFormat();
  const { config: appConfig } = useAppConfig();
  const currency = (appConfig?.currency || "USD").toUpperCase();

  const today = new Date();
  const pad   = n => String(n).padStart(2, "0");

  // ── Tab state ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("book"); // "book" | "mybookings"

  // ── Booking tab state ────────────────────────────────────────────────────────
  const [year,         setYear]        = useState(today.getFullYear());
  const [month,        setMonth]       = useState(today.getMonth());
  const [selectedDate, setSelectedDate]= useState(null);
  const [coachSlots,   setCoachSlots]  = useState([]);
  const [slotsLoading, setSlotsLoading]= useState(false);
  const [booking,      setBooking]     = useState(null); // {coach, slot}
  const [students,     setStudents]    = useState([]);
  const [studentId,    setStudentId]   = useState("");
  const [studentName,  setStudentName] = useState("");
  const [notes,        setNotes]       = useState("");
  const [msg,          setMsg]         = useState(null);
  const [submitting,   setSubmitting]  = useState(false);
  const [availDates,   setAvailDates]  = useState(new Set());
  const [savedCard,    setSavedCard]   = useState(null);

  // ── My Bookings tab state ────────────────────────────────────────────────────
  const [bkYear,    setBkYear]   = useState(today.getFullYear());
  const [bkMonth,   setBkMonth]  = useState(today.getMonth() + 1);
  const [myBookings,setMyBookings]= useState([]);
  const [bkLoading, setBkLoading]= useState(false);

  // ── Load saved card ───────────────────────────────────────────────────────────
  useEffect(() => {
    const pid = currentUser?.parent_id;
    if (!pid) return;
    fetch(`${API_BASE}/oto/parent-payment-method/${pid}`)
      .then(r => r.json())
      .then(d => setSavedCard(d.card || null))
      .catch(() => {});
  }, [currentUser]);

  // ── Linked students from currentUser ─────────────────────────────────────────
  useEffect(() => {
    const list = Array.isArray(currentUser?.linked_students) ? currentUser.linked_students : [];
    setStudents(list);
    if (list.length === 1) {
      setStudentId(String(list[0].student_id));
      setStudentName(`${list[0].first_name} ${list[0].last_name}`);
    }
  }, [currentUser]);

  // ── Fetch my bookings ────────────────────────────────────────────────────────
  const fetchMyBookings = useCallback(async (yr, mo) => {
    const linkedIds = (currentUser?.linked_students || []).map(s => s.student_id);
    if (!linkedIds.length) { setMyBookings([]); return; }
    setBkLoading(true);
    try {
      // Fetch bookings for each linked student and merge
      const results = await Promise.all(
        linkedIds.map(sid =>
          fetch(`${API_BASE}/oto/bookings?student_id=${sid}&month=${mo}&year=${yr}`)
            .then(r => r.json())
            .then(d => d.bookings || [])
            .catch(() => [])
        )
      );
      const merged = results.flat().sort((a, b) =>
        a.session_date > b.session_date ? 1 : a.session_date < b.session_date ? -1 :
        a.start_time > b.start_time ? 1 : -1
      );
      setMyBookings(merged);
    } catch { setMyBookings([]); }
    setBkLoading(false);
  }, [currentUser]);

  useEffect(() => {
    if (activeTab === "mybookings") fetchMyBookings(bkYear, bkMonth);
  }, [activeTab, bkYear, bkMonth, fetchMyBookings]);

  // ── Fetch available dates for the visible month (single request) ─────────────
  useEffect(() => {
    setAvailDates(new Set());
    fetch(`${API_BASE}/oto/available-dates?year=${year}&month=${month + 1}`)
      .then(r => r.json())
      .then(d => setAvailDates(new Set(d.available_dates || [])))
      .catch(() => setAvailDates(new Set()));
  }, [year, month]);

  const fetchSlotsForDate = useCallback(async (ds) => {
    setSlotsLoading(true); setCoachSlots([]);
    try {
      const res  = await fetch(`${API_BASE}/oto/all-coaches-slots?date=${ds}`);
      const data = await res.json();
      setCoachSlots(data.coaches || []);
    } catch { setCoachSlots([]); }
    setSlotsLoading(false);
  }, []);

  const handleDayClick = (d) => {
    if (!d) return;
    const ds = `${year}-${pad(month + 1)}-${pad(d)}`;
    setSelectedDate(ds); setBooking(null);
    fetchSlotsForDate(ds);
  };

  // Allow only current month and next month
  const maxMonth = today.getMonth() === 11 ? 0 : today.getMonth() + 1;
  const maxYear  = today.getMonth() === 11 ? today.getFullYear() + 1 : today.getFullYear();
  const isMinMonth = year === today.getFullYear() && month === today.getMonth();
  const isMaxMonth = year === maxYear && month === maxMonth;

  const prevMonth = () => {
    if (isMinMonth) return;
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
    setSelectedDate(null); setCoachSlots([]);
  };
  const nextMonth = () => {
    if (isMaxMonth) return;
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
    setSelectedDate(null); setCoachSlots([]);
  };

  const handleBook = async () => {
    if (!studentId) { setMsg({ ok: false, text: "Please select a student above first." }); return; }
    if (!booking)   { setMsg({ ok: false, text: "No slot selected." }); return; }
    setSubmitting(true); setMsg(null);
    try {
      const res  = await fetch(`${API_BASE}/oto/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          student_id:       Number(studentId),
          coach_id:         booking.coach.coach_id,
          session_date:     selectedDate,
          start_time:       booking.slot.start_time,
          end_time:         booking.slot.end_time,
          duration_minutes: 60,
          booked_by_type:   "parent",
          booked_by_id:     currentUser.parent_id || 0,
          notes,
        }),
      });
      const data = await res.json();
      if (data.success) {
        const confirmMsg = data.auto_charged
          ? `Booking confirmed — card charged ${currency} $${data.amount_due}.`
          : `Booking created (pending payment confirmation).`;
        setMsg({ ok: true, text: confirmMsg });
        setBooking(null); setNotes("");
        fetchSlotsForDate(selectedDate);
      } else {
        setMsg({ ok: false, text: data.message || "Booking failed." });
      }
    } catch { setMsg({ ok: false, text: "Network error." }); }
    setSubmitting(false);
  };

  // ── Styles ───────────────────────────────────────────────────────────────────
  const cardSt = {
    background: isDark ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.85)",
    border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,53,128,0.10)",
    borderRadius: 16, padding: 20, backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
  };
  const txt  = isDark ? "#e2e8f0" : "#1a2840";
  const dim  = isDark ? "rgba(255,255,255,0.45)" : "rgba(3,78,123,0.50)";
  const acc  = isDark ? getBrandColors().orange : getBrandColors().blue;

  const dayCell = (d, ds) => {
    const isToday    = ds === `${today.getFullYear()}-${pad(today.getMonth()+1)}-${pad(today.getDate())}`;
    const isSelected = ds === selectedDate;
    const hasSlots   = d && availDates.has(ds);
    return {
      width: 36, height: 36, borderRadius: "50%", display: "flex",
      alignItems: "center", justifyContent: "center",
      cursor: d ? "pointer" : "default", fontSize: 14, fontWeight: isToday ? 800 : 400,
      background: isSelected ? "#f59e0b" : hasSlots ? (isDark ? "rgba(245,158,11,0.25)" : "rgba(245,158,11,0.20)") : "transparent",
      color: isSelected ? "#fff" : d ? txt : "transparent",
      border: isToday && !isSelected ? "2px solid #f59e0b" : "2px solid transparent",
    };
  };

  const weeks = buildCalendar(year, month);

  // ── My Bookings helpers ───────────────────────────────────────────────────────
  const confirmedCount = myBookings.filter(b => ["confirmed","checked_in","completed"].includes(b.status)).length;
  const totalCount     = myBookings.length;
  const { sorted: sortedMyBookings, toggleSort: toggleMyBookingSort, sortArrow: myBookingSortArrow } = useSort(myBookings, "session_date");

  const prevBkMonth = () => { if (bkMonth === 1) { setBkYear(y => y-1); setBkMonth(12); } else setBkMonth(m => m-1); };
  const nextBkMonth = () => { if (bkMonth === 12) { setBkYear(y => y+1); setBkMonth(1); } else setBkMonth(m => m+1); };

  // ── Tab nav pill ─────────────────────────────────────────────────────────────
  const tabBtn = (id, label) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        padding: "8px 22px", borderRadius: 20, border: "none", cursor: "pointer",
        fontWeight: 700, fontSize: 13,
        background: activeTab === id ? "#f59e0b" : (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,128,0.07)"),
        color: activeTab === id ? "#fff" : txt, transition: "background 0.15s",
      }}
    >{label}</button>
  );

  return (
    <div style={{ padding: "24px 28px", fontFamily: "'Segoe UI', system-ui, sans-serif", color: txt }}>

      {/* ── Page header ── */}
      <h2 style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 800, color: acc }}>
        1-on-1 Sessions
      </h2>
      <p style={{ margin: "0 0 18px", fontSize: 13, opacity: 0.65 }}>
        Book coaching sessions and track your session history.
      </p>

      {/* ── Tab nav ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 22, flexWrap: "wrap" }}>
        {tabBtn("book",       "📅  Book a Session")}
        {tabBtn("mybookings", "📋  My Bookings")}
        {tabBtn("coachavail", "🗓  Coach Availability")}
      </div>

      {/* ════════════════════════════════════════════════════════ */}
      {/* TAB 1 — Book a Session                                  */}
      {/* ════════════════════════════════════════════════════════ */}
      {activeTab === "book" && (
        <>
          {/* ── Step 1: Student selector (always at top) ── */}
          <div style={{ ...cardSt, marginBottom: 20, padding: "16px 20px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", opacity: 0.6, marginBottom: 10 }}>
              Step 1 — Who is this session for?
            </div>
            {students.length === 0 ? (
              <div style={{ fontSize: 13, opacity: 0.55 }}>No linked students found.</div>
            ) : students.length === 1 ? (
              /* Single child — just show as confirmed */
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: isDark ? "rgba(245,158,11,0.20)" : "rgba(245,158,11,0.18)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, color: "#f59e0b" }}>
                  {students[0].first_name?.[0]}{students[0].last_name?.[0]}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: txt }}>{students[0].first_name} {students[0].last_name}</div>
                  <div style={{ fontSize: 11, opacity: 0.55 }}>Session will be booked for this student</div>
                </div>
                <div style={{ marginLeft: "auto", padding: "3px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: "rgba(16,185,129,0.12)", color: getBrandColors().green, border: "1px solid rgba(16,185,129,0.28)" }}>Selected</div>
              </div>
            ) : (
              /* Multiple children — show pill selector */
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {students.map(s => {
                  const sel = studentId === String(s.student_id);
                  return (
                    <button key={s.student_id}
                      onClick={() => { setStudentId(String(s.student_id)); setStudentName(`${s.first_name} ${s.last_name}`); setBooking(null); setMsg(null); }}
                      style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "8px 16px", borderRadius: 24, border: "none", cursor: "pointer",
                        fontWeight: 700, fontSize: 13, transition: "all 0.15s",
                        background: sel ? "#f59e0b" : (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,128,0.07)"),
                        color: sel ? "#fff" : txt,
                        boxShadow: sel ? "0 2px 10px rgba(245,158,11,0.35)" : "none",
                      }}>
                      <span style={{ width: 24, height: 24, borderRadius: "50%", background: sel ? "rgba(255,255,255,0.25)" : "rgba(245,158,11,0.20)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 900 }}>
                        {s.first_name?.[0]}
                      </span>
                      {s.first_name} {s.last_name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Step 2: Calendar + Slots ── */}
          <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 20, alignItems: "start" }}>

            {/* Calendar */}
            <div style={{ ...cardSt }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", opacity: 0.6, marginBottom: 12 }}>
                Step 2 — Pick a date
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <button onClick={prevMonth} disabled={isMinMonth} style={{ background: "none", border: "none", fontSize: 20, color: isMinMonth ? "rgba(128,128,128,0.25)" : acc, cursor: isMinMonth ? "default" : "pointer", lineHeight: 1 }}>‹</button>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{MONTHS[month]} {year}</span>
                <button onClick={nextMonth} disabled={isMaxMonth} style={{ background: "none", border: "none", fontSize: 20, color: isMaxMonth ? "rgba(128,128,128,0.25)" : acc, cursor: isMaxMonth ? "default" : "pointer", lineHeight: 1 }}>›</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", textAlign: "center", marginBottom: 6 }}>
                {DAYS.map(d => <span key={d} style={{ fontSize: 10, fontWeight: 700, opacity: 0.5 }}>{d}</span>)}
              </div>
              {weeks.map((week, wi) => (
                <div key={wi} style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", textAlign: "center", marginBottom: 2 }}>
                  {week.map((d, di) => {
                    const ds = d ? `${year}-${pad(month+1)}-${pad(d)}` : "";
                    return (
                      <div key={di} style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 36 }}>
                        <div style={dayCell(d, ds)} onClick={() => handleDayClick(d)}>{d || ""}</div>
                      </div>
                    );
                  })}
                </div>
              ))}
              <div style={{ marginTop: 10, fontSize: 10, opacity: 0.55, display: "flex", gap: 10 }}>
                <span><span style={{ display:"inline-block", width:10, height:10, borderRadius:"50%", background:"rgba(245,158,11,0.40)", marginRight:4, verticalAlign:"middle" }}/>Has slots</span>
                <span><span style={{ display:"inline-block", width:10, height:10, borderRadius:"50%", background:"#f59e0b", marginRight:4, verticalAlign:"middle" }}/>Selected</span>
              </div>
            </div>

            {/* Slots panel */}
            <div>
              {!selectedDate && (
                <div style={{ ...cardSt, textAlign: "center", padding: "48px 20px", opacity: 0.55 }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>📅</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>Select a date to see available coaches and time slots</div>
                </div>
              )}
              {selectedDate && slotsLoading && (
                <div style={{ ...cardSt, textAlign: "center", padding: 40, opacity: 0.6 }}>
                  <div style={{ fontSize: 13 }}>Loading slots for {selectedDate}…</div>
                </div>
              )}
              {selectedDate && !slotsLoading && coachSlots.length === 0 && (
                <div style={{ ...cardSt, textAlign: "center", padding: 40, opacity: 0.6 }}>
                  No available slots on {selectedDate}
                </div>
              )}
              {selectedDate && !slotsLoading && coachSlots.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, opacity: 0.75 }}>Step 3 — Pick a time slot · {fmtDate(selectedDate)}</div>
                  {coachSlots.map(coach => (
                    <div key={coach.coach_id} style={{ ...cardSt, padding: "16px 18px" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 15 }}>{coach.coach_name}</div>
                          <div style={{ fontSize: 11, opacity: 0.60, marginTop: 2 }}>{currency} ${coach.hourly_rate}/hr · 60 min sessions</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {coach.slots.map((slot, si) => {
                          const isSel = booking?.coach.coach_id === coach.coach_id && booking?.slot.start_time === slot.start_time;
                          return (
                            <button key={si}
                              onClick={() => { if (!studentId) { setMsg({ ok: false, text: "Please select a student first (Step 1 above)." }); return; } setBooking({ coach, slot }); setMsg(null); }}
                              style={{
                                padding: "8px 16px", borderRadius: 8, border: isSel ? "none" : `1.5px solid ${isDark ? "rgba(255,255,255,0.15)" : "rgba(0,53,128,0.18)"}`,
                                cursor: "pointer", fontWeight: isSel ? 700 : 500, fontSize: 13,
                                background: isSel ? "#f59e0b" : (isDark ? "rgba(255,255,255,0.07)" : "rgba(0,53,128,0.05)"),
                                color: isSel ? "#fff" : (isDark ? "#e2e8f0" : getBrandColors().blue),
                                transition: "all 0.15s",
                              }}>
                              {fmtTime(slot.start_time)} – {fmtTime(slot.end_time)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Step 4: Confirm panel (appears after slot selected) ── */}
          {booking && (
            <div style={{ ...cardSt, marginTop: 20, padding: "20px 24px" }}>
              {/* Header row */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 16, color: acc }}>Confirm Booking</div>
                  <div style={{ fontSize: 12, opacity: 0.65, marginTop: 3 }}>
                    {booking.coach.coach_name} · {fmtDate(selectedDate)} · {fmtTime(booking.slot.start_time)}–{fmtTime(booking.slot.end_time)} · <strong>{currency} ${booking.slot.amount_due}</strong>
                  </div>
                </div>
                <button onClick={() => { setBooking(null); setMsg(null); }}
                  style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", opacity: 0.45, color: txt }}>✕</button>
              </div>

              {/* Student confirmed row */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 8, marginBottom: 14,
                background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,53,128,0.04)", border: `1px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(0,53,128,0.10)"}` }}>
                <div style={{ width: 30, height: 30, borderRadius: "50%", background: "rgba(245,158,11,0.20)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, color: "#f59e0b", flexShrink: 0 }}>
                  {studentName?.[0]}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: txt }}>{studentName}</div>
                  <div style={{ fontSize: 11, opacity: 0.55 }}>Student for this session</div>
                </div>
              </div>

              {/* Notes */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, marginBottom: 6, textTransform: "uppercase", opacity: 0.6 }}>Notes (optional)</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                  placeholder="Session goals, focus areas…"
                  style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: `1.5px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,53,128,0.18)"}`,
                    background: isDark ? "rgba(255,255,255,0.05)" : "#fff", color: txt, fontSize: 13, boxSizing: "border-box", resize: "vertical", outline: "none" }} />
              </div>

              {/* Payment */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, marginBottom: 8, textTransform: "uppercase", opacity: 0.6 }}>Payment</label>
                {savedCard ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 14,
                    background: isDark ? "rgba(16,185,129,0.10)" : "rgba(16,185,129,0.07)",
                    border: "1.5px solid rgba(16,185,129,0.30)", borderRadius: 10, padding: "12px 16px" }}>
                    <div style={{ flexShrink: 0, width: 48, height: 30, borderRadius: 6,
                      background: isDark ? "rgba(255,255,255,0.10)" : "rgba(3,78,123,0.08)",
                      border: isDark ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(3,78,123,0.15)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 9, fontWeight: 800, color: isDark ? "#94a3b8" : "#475569", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                      {savedCard.brand || "CARD"}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1a2840", letterSpacing: "0.06em" }}>
                        •••• •••• •••• {savedCard.last4}
                      </div>
                      <div style={{ fontSize: 11, color: isDark ? "#94a3b8" : "#64748b", marginTop: 2, display: "flex", gap: 12 }}>
                        <span>{currentUser?.full_name || "Card Holder"}</span>
                        <span>Exp {String(savedCard.exp_month).padStart(2,"0")}/{savedCard.exp_year}</span>
                      </div>
                    </div>
                    <div style={{ flexShrink: 0, textAlign: "center", padding: "5px 12px", borderRadius: 8,
                      background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.28)",
                      fontSize: 12, fontWeight: 700, color: getBrandColors().green, lineHeight: 1.5 }}>
                      {currency} ${booking.slot.amount_due}<br />
                      <span style={{ fontSize: 10, fontWeight: 400 }}>charged on confirm</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ background: isDark ? "rgba(245,158,11,0.12)" : "rgba(245,158,11,0.09)",
                    border: "1.5px solid rgba(245,158,11,0.35)", borderRadius: 10, padding: "12px 16px",
                    fontSize: 12, color: isDark ? getBrandColors().orange : "#92400e" }}>
                    ⚠️ No card on file. Go to <strong>My Child → Payment Method</strong> to save a card first. Booking will be pending until admin confirms payment.
                  </div>
                )}
              </div>

              {msg && (
                <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: 13, fontWeight: 600,
                  background: msg.ok ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.10)",
                  color: msg.ok ? getBrandColors().green : getBrandColors().red, border: `1px solid ${msg.ok ? "rgba(16,185,129,0.25)" : "rgba(239,68,68,0.20)"}` }}>
                  {msg.text}
                </div>
              )}

              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={handleBook} disabled={submitting || !studentId}
                  style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: "none",
                    cursor: submitting || !studentId ? "not-allowed" : "pointer",
                    background: submitting || !studentId ? "rgba(245,158,11,0.40)" : "#f59e0b",
                    color: "#fff", fontWeight: 800, fontSize: 14 }}>
                  {submitting ? "Confirming…" : savedCard ? `Pay & Book — ${currency} $${booking.slot.amount_due}` : "Request Booking"}
                </button>
                <button onClick={() => { setBooking(null); setMsg(null); }}
                  style={{ padding: "12px 20px", borderRadius: 10, border: `1.5px solid ${isDark ? "rgba(255,255,255,0.15)" : "rgba(0,53,128,0.18)"}`,
                    cursor: "pointer", background: "transparent", color: txt, fontWeight: 600, fontSize: 13 }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Top-level msg (e.g. student-not-selected warning) */}
          {msg && !booking && (
            <div style={{ marginTop: 14, padding: "10px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: "rgba(239,68,68,0.10)", color: getBrandColors().red, border: "1px solid rgba(239,68,68,0.20)" }}>
              {msg.text}
            </div>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════════ */}
      {/* TAB 2 — My Bookings                                     */}
      {/* ════════════════════════════════════════════════════════ */}
      {activeTab === "mybookings" && (
        <div>
          {/* Month navigator */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
            <button onClick={prevBkMonth} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: acc }}>‹</button>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{MONTHS_SHORT[bkMonth - 1]} {bkYear}</span>
            <button onClick={nextBkMonth} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: acc }}>›</button>
          </div>

          {/* KPI summary cards */}
          <div style={{ display: "flex", gap: 14, marginBottom: 22, flexWrap: "wrap" }}>
            {[
              { label: "Total Bookings",     value: totalCount,     color: getBrandColors().blue },
              { label: "Confirmed / Done",   value: confirmedCount, color: getBrandColors().green },
              { label: "Pending Payment",    value: myBookings.filter(b => b.status === "pending_payment").length, color: "#ca8a04" },
              { label: "Cancelled",          value: myBookings.filter(b => b.status === "cancelled").length, color: getBrandColors().red },
            ].map(kpi => (
              <div key={kpi.label} style={{ ...cardSt, minWidth: 150, flex: 1, textAlign: "center", padding: "16px 18px" }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: kpi.color }}>{kpi.value}</div>
                <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.65, marginTop: 4 }}>{kpi.label}</div>
              </div>
            ))}
          </div>

          {/* Bookings table */}
          {bkLoading ? (
            <div style={{ ...cardSt, textAlign: "center", padding: 40, opacity: 0.6 }}>Loading…</div>
          ) : myBookings.length === 0 ? (
            <div style={{ ...cardSt, textAlign: "center", padding: 40, opacity: 0.6 }}>
              No bookings found for {MONTHS_SHORT[bkMonth - 1]} {bkYear}
            </div>
          ) : (
            <div style={{ ...cardSt, padding: 0, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,53,128,0.06)" }}>
                      {[
                        { label: "Date", key: "session_date" },
                        { label: "Coach", key: "coach_name" },
                        { label: "Student", key: "first_name" },
                        { label: "Time", key: "start_time" },
                        { label: "Status", key: "status" },
                      ].map(h => (
                        <th key={h.label} onClick={() => toggleMyBookingSort(h.key)} style={{ padding: "11px 14px", textAlign: "left", fontWeight: 700,
                          fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase",
                          color: dim, borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,128,0.10)"}`,
                          cursor: "pointer", userSelect: "none" }}>
                          {h.label}{myBookingSortArrow(h.key)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedMyBookings.map(b => {
                      const sm = STATUS_META[b.status] || { label: b.status, tone: "neutral" };
                      const divider = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,53,128,0.07)";
                      return (
                        <tr key={b.booking_id}>
                          <td style={{ padding: "11px 14px", borderBottom: `1px solid ${divider}`, fontWeight: 600 }}>{fmtDate(b.session_date)}</td>
                          <td style={{ padding: "11px 14px", borderBottom: `1px solid ${divider}` }}>{b.coach_name}</td>
                          <td style={{ padding: "11px 14px", borderBottom: `1px solid ${divider}` }}>{b.first_name} {b.last_name}</td>
                          <td style={{ padding: "11px 14px", borderBottom: `1px solid ${divider}`, whiteSpace: "nowrap" }}>
                            {fmtTime(b.start_time)} – {fmtTime(b.end_time)}
                          </td>
                          <td style={{ padding: "11px 14px", borderBottom: `1px solid ${divider}` }}>
                            <StatusBadge label={sm.label} tone={sm.tone} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════ */}
      {/* TAB 3 — Coach Availability                              */}
      {/* ════════════════════════════════════════════════════════ */}
      {activeTab === "coachavail" && <CoachAvailabilityView />}

    </div>
  );
}
